/**
 * Broker Accounts Redux Slice
 * Manages broker account state (accounts list, selected accounts, sync status)
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this slice only stores broker account data from backend.
 * All broker account operations are handled by backend Broker Integration Service.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BrokerAccount } from '../../types/brokerAccount';
import type { BrokerAccountResponse } from '../../api/generated';

export interface BrokerAccountsState {
  accounts: BrokerAccount[];
  selectedAccountIds: string[];
  syncStatus: Record<string, 'syncing' | 'synced' | 'error'>;
  loading: boolean;
  error: string | null;
}

const initialState: BrokerAccountsState = {
  accounts: [],
  selectedAccountIds: [],
  syncStatus: {},
  loading: false,
  error: null,
};

/**
 * Fetch broker accounts from backend
 * ⚠️ IMPORTANT: Per ADR-FE-001, this only fetches data. Backend provides all account information.
 */
export const fetchBrokerAccounts = createAsyncThunk(
  'brokerAccounts/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.brokerAccountsList();
      
      // Backend always returns: { accounts: [], total: 0, ... } or { accounts: [...], total: N, ... }
      // Structure is consistent whether there are 0 accounts or more
      if (!response?.data) {
        return rejectWithValue('Invalid response structure: missing data');
      }
      
      // Backend guarantees accounts is always an array (empty [] when no accounts)
      const accountsArray = response.data.accounts || [];
      
      if (!Array.isArray(accountsArray)) {
        return rejectWithValue('Invalid response structure: accounts is not an array');
      }
      
      // Map backend response to frontend types
      // Note: acc.id is the Account UUID (from broker_accounts.accounts.id)
      // acc.account_id is the broker's string identifier (like "AB1234")
      // We use acc.id (UUID) because portfolio service expects UUID, not the broker's string identifier
      const accounts: BrokerAccount[] = accountsArray.map((acc: BrokerAccountResponse) => {
        const mappedAccount = {
          id: acc.id || acc.account_id || '', // Use UUID (acc.id), fallback to account_id for backwards compatibility
          broker_account_id: acc.account_id || '',
          broker_name: acc.broker_type as BrokerAccount['broker_name'],
          account_name: acc.account_name || '',
          balance: acc.account_balance ?? acc.accountBalance ?? 0, // Map account_balance from backend to balance
          active: acc.status === 'active' || acc.status?.toLowerCase() === 'connected',
          status: acc.status || '', // Preserve raw status value from database
          linked_at: acc.created_at || '',
          auth_mode: (acc as any).auth_mode as 'oauth' | 'totp_automated' | undefined, // Map auth_mode from backend
          totp_enabled: (acc as any).totp_enabled as boolean | undefined, // Map totp_enabled from backend
        };
        
        return mappedAccount;
      });
      
      return accounts;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch broker accounts';
      console.error('[fetchBrokerAccounts] Error fetching broker accounts:', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get supported brokers from backend
 */
export const fetchSupportedBrokers = createAsyncThunk(
  'brokerAccounts/fetchSupported',
  async (_, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.brokerAccountsSupportedBrokers();
      return response.data;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch supported brokers';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Disconnect broker account
 */
export const disconnectBrokerAccount = createAsyncThunk(
  'brokerAccounts/disconnect',
  async (registrationId: string, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      await api.brokerAccountDisconnect({ registration_id: registrationId });
      return registrationId;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to disconnect broker account';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Deregister broker account (permanent deletion)
 */
export const deregisterBrokerAccount = createAsyncThunk(
  'brokerAccounts/deregister',
  async (registrationId: string, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.brokerRegistrationDeregister(registrationId);
      return response.data;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to deregister broker account';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Reconnect broker account
 */
export const reconnectBrokerAccount = createAsyncThunk(
  'brokerAccounts/reconnect',
  async (params: { registrationId: string; forceRefresh?: boolean }, { rejectWithValue }) => {
    try {
      const { api } = await import('../../api/client');
      const response = await api.brokerRegistrationReconnect({
        registration_id: params.registrationId,
        force_refresh: params.forceRefresh || false
      });
      return response.data;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reconnect broker account';
      return rejectWithValue(errorMessage);
    }
  }
);

export const brokerAccountsSlice = createSlice({
  name: 'brokerAccounts',
  initialState,
  reducers: {
    setAccounts: (state, action) => {
      state.accounts = action.payload;
    },
    toggleAccountSelection: (state, action: { payload: string }) => {
      const accountId = action.payload;
      if (state.selectedAccountIds.includes(accountId)) {
        state.selectedAccountIds = state.selectedAccountIds.filter((id) => id !== accountId);
      } else {
        state.selectedAccountIds.push(accountId);
      }
    },
    selectAllAccounts: (state) => {
      state.selectedAccountIds = state.accounts.map((acc) => acc.id);
    },
    deselectAllAccounts: (state) => {
      state.selectedAccountIds = [];
    },
    setSyncStatus: (state, action: { payload: { accountId: string; status: 'syncing' | 'synced' | 'error' } }) => {
      state.syncStatus[action.payload.accountId] = action.payload.status;
    },
    setLoading: (state, action: { payload: boolean }) => {
      state.loading = action.payload;
    },
    setError: (state, action: { payload: string | null }) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch broker accounts
      .addCase(fetchBrokerAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBrokerAccounts.fulfilled, (state, action) => {
        state.accounts = action.payload || [];
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchBrokerAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Disconnect broker account
      .addCase(disconnectBrokerAccount.fulfilled, (state, action) => {
        // Remove disconnected account from list
        state.accounts = state.accounts.filter((acc) => acc.id !== action.payload);
        // Remove from selected if selected
        state.selectedAccountIds = state.selectedAccountIds.filter((id) => id !== action.payload);
      })
      .addCase(disconnectBrokerAccount.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Deregister broker account
      .addCase(deregisterBrokerAccount.fulfilled, (state, action) => {
        // Remove deregistered account from list
        state.accounts = state.accounts.filter((acc) => acc.id !== action.payload.id);
        // Remove from selected if selected
        state.selectedAccountIds = state.selectedAccountIds.filter((id) => id !== action.payload.id);
      })
      .addCase(deregisterBrokerAccount.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Reconnect broker account
      .addCase(reconnectBrokerAccount.pending, (state) => {
        // Set loading state if needed
        state.error = null;
      })
      .addCase(reconnectBrokerAccount.fulfilled, (state) => {
        // If OAuth URL is returned, the redirect will be handled by the UI component
        // No state update needed here as the OAuth flow will handle the reconnection
        state.error = null;
      })
      .addCase(reconnectBrokerAccount.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setAccounts,
  toggleAccountSelection,
  selectAllAccounts,
  deselectAllAccounts,
  setSyncStatus,
  setLoading,
  setError,
} = brokerAccountsSlice.actions;
export default brokerAccountsSlice.reducer;

